import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
    login,
    signUp,
    saveTokenInLocalStorage,
    runLogoutTimer,
    formatError,
} from '../api/auth-service';

interface User {
    id?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    phoneNumber?: string;
}

interface AuthState {
    user: User | null;
    token: string | null;
    isLoading: boolean;
    error: string | null;
    isAuthenticated: boolean;
}

const initialState: AuthState = {
    user: null,
    token: null,
    isLoading: false,
    error: null,
    isAuthenticated: false,
};

// Async thunks
export const loginUser = createAsyncThunk(
    'auth/login',
    async (
        { email, password, navigate }: { email: string; password: string; navigate: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await login({ email, password });
            const normalized = saveTokenInLocalStorage(response.data);
            const expireDate = normalized.expireDate
                ? new Date(normalized.expireDate)
                : new Date(Date.now() + 60 * 60 * 1000);

            runLogoutTimer(
                dispatch,
                Math.max(expireDate.getTime() - Date.now(), 0),
                () => dispatch(logoutUser({ navigate }))
            );

            navigate('/dashboard');
            return normalized;
        } catch (error: any) {
            const errorMessage = formatError(error?.response?.data ?? error);
            return rejectWithValue(errorMessage);
        }
    }
);

export const signupUser = createAsyncThunk(
    'auth/signup',
    async (
        { payload, navigate }: { payload: any; navigate: any },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await signUp(payload);
            const normalized = saveTokenInLocalStorage(response.data);
            const expireDate = normalized.expireDate
                ? new Date(normalized.expireDate)
                : new Date(Date.now() + 60 * 60 * 1000);

            runLogoutTimer(
                dispatch,
                Math.max(expireDate.getTime() - Date.now(), 0),
                () => dispatch(logoutUser({ navigate }))
            );

            navigate('/dashboard');
            return normalized;
        } catch (error: any) {
            const errorMessage = formatError(error?.response?.data ?? error);
            return rejectWithValue(errorMessage);
        }
    }
);

const authSlice = createSlice({
    name: 'auth',
    initialState,
    reducers: {
        logoutUser: (state, action: PayloadAction<{ navigate: any }>) => {
            localStorage.removeItem('userDetails');
            localStorage.removeItem('accessToken');
            state.user = null;
            state.token = null;
            state.isAuthenticated = false;
            action.payload.navigate('/login');
        },
        clearError: (state) => {
            state.error = null;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
        },
    },
    extraReducers: (builder) => {
        // Login
        builder
            .addCase(loginUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(loginUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.token = action.payload.token || null;
                state.user = action.payload.user || null;
            })
            .addCase(loginUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });

        // Signup
        builder
            .addCase(signupUser.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(signupUser.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isAuthenticated = true;
                state.token = action.payload.token || null;
                state.user = action.payload.user || null;
            })
            .addCase(signupUser.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            });
    },
});

export const { logoutUser, clearError, setLoading } = authSlice.actions;
export default authSlice.reducer;
